// ============================================
// AUTHENTICATION HELPERS
// ============================================
// Note: With cookie-based auth, we don't manage tokens manually
// The backend sets HTTP-only cookies automatically

export const setUser = (user) => {
  localStorage.setItem('dira-user', JSON.stringify(user));
};

export const getUser = () => {
  const user = localStorage.getItem('dira-user');
  return user ? JSON.parse(user) : null;
};

export const removeUser = () => {
  localStorage.removeItem('dira-user');
};

export const isAuthenticated = () => {
  // Check if user data exists in localStorage
  // The actual authentication is handled by cookies
  return !!getUser();
};

export const getUserRole = () => {
  const user = getUser();
  return user?.role || null;
};

// ============================================
// FORMATTING HELPERS
// ============================================
export const formatCurrency = (amount, currency = 'KES') => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const formatDate = (date, options = {}) => {
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return new Intl.DateTimeFormat('en-KE', { ...defaultOptions, ...options }).format(new Date(date));
};

export const formatTime = (date) => {
  return new Intl.DateTimeFormat('en-KE', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
};

export const formatDateTime = (date) => {
  return `${formatDate(date)} at ${formatTime(date)}`;
};

export const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// ============================================
// VALIDATION HELPERS
// ============================================
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
};

export const validatePhone = (phone) => {
  // Kenyan phone number format
  const re = /^(\+254|0)[17]\d{8}$/;
  return re.test(String(phone));
};

export const validatePassword = (password) => {
  // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
  const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
  return re.test(password);
};

export const getPasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password)) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[@$!%*?&]/.test(password)) strength++;
  
  if (strength <= 2) return { level: 'weak', color: '#EF4444' };
  if (strength <= 4) return { level: 'medium', color: '#F59E0B' };
  return { level: 'strong', color: '#10B981' };
};

// ============================================
// UTILITY HELPERS
// ============================================
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
};

export const downloadFile = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// ============================================
// ARRAY HELPERS
// ============================================
export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const group = item[key];
    if (!result[group]) {
      result[group] = [];
    }
    result[group].push(item);
    return result;
  }, {});
};

export const sortBy = (array, key, order = 'asc') => {
  return [...array].sort((a, b) => {
    if (order === 'asc') {
      return a[key] > b[key] ? 1 : -1;
    } else {
      return a[key] < b[key] ? 1 : -1;
    }
  });
};

export const uniqueBy = (array, key) => {
  return [...new Map(array.map(item => [item[key], item])).values()];
};

// ============================================
// ROLE-BASED HELPERS
// ============================================
export const ROLES = {
  STUDENT: 'student',
  PARENT: 'parent',
  TEACHER: 'teacher',
  PUBLISHER: 'publisher',
  ADMIN: 'admin',
  OWNER: 'owner',
};

export const hasRole = (requiredRole) => {
  const userRole = getUserRole();
  return userRole === requiredRole;
};

export const hasAnyRole = (roles) => {
  const userRole = getUserRole();
  return roles.includes(userRole);
};

export const canAccessRoute = (route, userRole) => {
  const roleRoutes = {
    [ROLES.STUDENT]: ['/learner'],
    [ROLES.PARENT]: ['/parent'],
    [ROLES.TEACHER]: ['/teacher'],
    [ROLES.PUBLISHER]: ['/publisher'],
    [ROLES.ADMIN]: ['/admin'],
    [ROLES.OWNER]: ['/owner'],
  };
  
  return roleRoutes[userRole]?.some(r => route.startsWith(r)) || false;
};

